import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { REGISTRATION_OPEN } from './registrationConfig';
import { safeExternalRedirect, safeInternalPath } from './redirectSafety';
import { safeDccContinuation, withDccContinuation } from './authContinuation';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const redirect = new URLSearchParams(window.location.search).get('redirect');
  const dccContinuation = safeDccContinuation(redirect);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // A DCC flow resumes only through the exact signed backend resume endpoint.
      if (dccContinuation) { window.location.assign(dccContinuation); return; }
      // Preserve existing approved website/external navigation outside DCC authorization.
      const external = safeExternalRedirect(redirect);
      if (external) { window.location.assign(external); return; }
      navigate(safeInternalPath(redirect) || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Visinvoke</div>
        <h2 style={styles.title}>Sign In</h2>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" value={password}
              onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Please wait...' : 'Sign In'}
          </button>
        </form>
        <p style={styles.forgot}>
          <span style={styles.forgotLink} onClick={() => navigate(withDccContinuation('/reset-password', dccContinuation))}>
            Forgot password?
          </span>
        </p>
        {REGISTRATION_OPEN ? (
          <div style={styles.createBlock}>
            <p style={styles.createPrompt}>Don't have a VISINVOKE account?</p>
            <button type="button" style={styles.createBtn} onClick={() => navigate(withDccContinuation('/register', dccContinuation))}>
              Create Account
            </button>
            <p style={styles.createNote}>
              A VISINVOKE account is required to access all VISINVOKE services.
              VISINVOKE Desktop Command connects your account to your desktop products.
            </p>
          </div>
        ) : (
          <p style={styles.notice}>
            Registration is not yet open. Visit <span style={styles.highlight}>visinvoke.com</span> to be notified at launch.
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #334155, #881337)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { background: '#fff', borderRadius: 12, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  logo: { fontWeight: 900, fontSize: 22, color: '#8b5cf6', marginBottom: 8 },
  title: { margin: '0 0 24px', fontSize: 20, fontWeight: 700, color: '#1a1a1a' },
  error: { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  field: { marginBottom: 16 },
  label: { display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 },
  input: { width: '100%', border: '2px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' },
  btn: { width: '100%', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 },
  forgot: { textAlign: 'center', marginTop: 16, fontSize: 13 },
  forgotLink: { color: '#8b5cf6', fontWeight: 600, cursor: 'pointer' },
  notice: { textAlign: 'center', marginTop: 20, fontSize: 13, color: '#666', lineHeight: 1.6 },
  highlight: { color: '#8b5cf6', fontWeight: 600 },
  createBlock: { textAlign: 'center', marginTop: 22, paddingTop: 18, borderTop: '1px solid #eee' },
  createPrompt: { fontSize: 13, color: '#374151', fontWeight: 600, margin: '0 0 10px' },
  createBtn: { display: 'inline-block', background: '#fff', color: '#8b5cf6', border: '2px solid #8b5cf6', borderRadius: 8, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  createNote: { fontSize: 12, color: '#666', lineHeight: 1.6, margin: '12px 0 0' }
};
